<script lang="ts">
	import { page } from '$app/state';
	import ArticleContent from '$lib/ArticleContent.svelte';
	import ArticleCta from '$lib/ArticleCta.svelte';
	import Button from '$lib/Button.svelte';
	import CommentsSection from '$lib/CommentsSection.svelte';
	import Container from '$lib/Container.svelte';
	import FollowAndSubscription from '$lib/FollowAndSubscription.svelte';
	import RegistrationPopup from '$lib/RegistrationPopup.svelte';
	import SiderailBanner from '$lib/SiderailBanner.svelte';
	import SocialShare from '$lib/SocialShare.svelte';
	import SponsorBanner from '$lib/SponsorBanner.svelte';
	import StickyBanner from '$lib/StickyBanner.svelte';
	import StickyCta from '$lib/StickyCta.svelte';
	import StickyPromo from '$lib/StickyPromo.svelte';
	import StrapiImage from '$lib/StrapiImage.svelte';
	import TableOfContents from '$lib/TableOfContents.svelte';
	import { scrollProgressStore } from '$lib/stores/scrollProgress';
	import { userStore } from '$lib/stores/user';
	import { getSignedColor } from '$lib/utils';
	import { fetchSymbols } from '$lib/utils/symbols';
	import dayjs from 'dayjs';
	import { some } from 'lodash-es';
	import { BadgeCheck, MessageCircle, MessageCircleQuestion } from 'lucide-svelte';
	import { onMount } from 'svelte';
	import BeansproutReports from '../../quote/[ticker]/BeansproutReports.svelte';
	import { getArticleDates } from '../utils';
	import { type PageData } from './$types';

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();
	let { article, comments, symbol } = $derived(data);
	let {
		type,
		author,
		title,
		description,
		content,
		cover,
		themes,
		stickyCta,
		disableArticleAd,
		sponsorLogo,
		createdAt,
		publishedAt,
		cta
	} = $derived(article);
	let { name: category, uid: categoryUid } = $derived(article.category);
	let { dateModified } = $derived(getArticleDates(createdAt, publishedAt));
	let beansproutReportsCount = $state(-1);

	async function getRelatedArticles(documentId: string) {
		const relatedArticles = await fetch(`/api/related-articles/${documentId}`).then(
			(res) => res.json() as unknown as Populated<BaseArticle>[]
		);
		return relatedArticles;
	}

	async function getTrendingArticles() {
		const relatedArticles = await fetch('/api/trending-articles').then(
			(res) => res.json() as unknown as Populated<BaseArticle, never>[]
		);
		return relatedArticles;
	}

	let referencedSymbols: Populated<BaseSymbol, never>[] = $state([]);

	onMount(() => {
		window.addEventListener(
			'message',
			(ev: { data: { type: string; payload: { id: string; src: string; height: number } } }) => {
				const iframe = document.querySelectorAll('iframe');
				if (ev.data.type === 'resize-iframe') {
					iframe.forEach((el) => {
						if (el.src === ev.data.payload.src) {
							el.style.height = `${ev.data.payload.height}px`;
						}
					});
				} else if (ev.data.type === 'scroll-to') {
					const iframe: HTMLIFrameElement | null = document.querySelector(`#${ev.data.payload.id}`);
					if (iframe) {
						window.scrollTo({
							top: iframe.offsetTop + ev.data.payload.height,
							behavior: 'smooth'
						});
					}
				}
			},
			false
		);

		if (!sponsorLogo) {
			const externalLinks = document.querySelectorAll('a');
			externalLinks.forEach((link) => {
				if (link.hostname !== window.location.hostname) {
					link.target = '_blank';
					link.rel = 'noopener noreferrer';
				}
			});
		}
	});

	let scrollElement: HTMLElement | undefined = $state();

	function handleOnScroll() {
		if (!scrollElement) return;
		const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
		const height = scrollElement.scrollHeight - document.documentElement.clientHeight;
		const scrolled = (winScroll / height) * 100;
		$scrollProgressStore = Math.max(1, scrolled);
	}
</script>

<svelte:head>
	<script
		type="text/javascript"
		id="dianomi_context_script"
		src="https://www.dianomi.com/js/contextfeed.js"
	></script>
</svelte:head>

<svelte:window onscroll={handleOnScroll} />

{#if categoryUid !== 'promo' && !$userStore.signedIn}
	{#if some( themes, ({ uid }) => ['stocks', 'reits', 'etfs', 'singapore-depository-receipts', 'etfs-101', 'stocks-101'].includes(uid) )}
		<RegistrationPopup context="tool" delay={title?.includes('Quick Take') ? 15 : 20} />
	{:else}
		<RegistrationPopup context="article" delay={title?.includes('Quick Take') ? 15 : 20} />
	{/if}
{/if}

<div class="bg-white-pure" bind:this={scrollElement}>
	<Container wrapperClass="page-py">
		<div class="block lg:flex lg:gap-16">
			<article class="w-full">
				<header class="mb-6 max-w-screen-md">
					<h1 class="h1-serif mb-4">{title}</h1>
					<div class="divide-gray-light mb-4 flex items-center divide-x">
						<div class="flex items-center gap-3 pr-3">
							<a
								href="/{categoryUid}"
								class="bg-green-primary text-white-pure block rounded-md px-2 py-1 text-center text-xs font-medium"
							>
								{category}
							</a>
							{#if themes.length}
								<p class="small-bold text-gray-mid uppercase">
									{themes.map(({ name }) => name).join(', ')}
								</p>
							{/if}
						</div>
						{#if sponsorLogo}
							<div class="flex items-center gap-2 pl-3">
								<p class="small-bold text-green-dark">Powered by</p>
								<StrapiImage cover={sponsorLogo} class="h-8 w-auto" />
							</div>
						{/if}
					</div>
					<p class="index-regular text-gray-mid mb-2">
						By <a href={`/authors/${author.uid}`} class="link-primary">{author.name}</a>
						•
						{dayjs(dateModified).format('DD MMM YYYY')}
					</p>
					<div class="mb-6 flex gap-1">
						<BadgeCheck class="fill-blue-primary text-white-pure h-5 w-5 shrink-0" />
						<p class="index-regular text-gray-mid">
							Why trust Beansprout? We’re licensed by the Monetary Authority of Singapore (MAS).
						</p>
					</div>
					<div class="divide-gray-faint flex gap-3 divide-x">
						<SocialShare {title} href={page.url.href} />
						<a
							href="#feedback"
							class="index-bold text-green-primary flex items-center pl-3 leading-none"
						>
							{#if type === 'ProductReview'}
								<MessageCircleQuestion class="text-green-primary mr-1 h-4 w-4" />
								Questions
								{#if comments.length}
									({comments.length})
								{/if}
							{:else}
								<MessageCircle class="text-green-primary mr-1 h-4 w-4" />
								Comments {#if comments.length}
									({comments.length})
								{/if}
							{/if}
						</a>
					</div>
				</header>

				<div class="max-w-screen-md">
					<p class="mb-3">
						{description}
					</p>
					<StrapiImage
						{cover}
						class="section-mb mx-auto aspect-16/9 w-full rounded-2xl object-cover"
					/>

					<TableOfContents />

					{#await fetchSymbols(referencedSymbols) then symbolQuotes}
						{#if symbolQuotes.length}
							<section class="section-mb">
								<header class="body-bold mb-3">Referenced Symbols</header>
								<div class="mb-5 flex flex-wrap gap-2">
									{#each symbolQuotes as quote}
										<Button
											to={`/quote/${quote.symbol.exchangeTicker}`}
											variant="ghost"
											size="small"
										>
											<span class="small-bold text-black">
												{quote.symbol.exchangeTicker.split('.')[0]}
											</span>
											<span
												class="small-regular ml-1"
												style="color: var(--color-{getSignedColor(
													quote.regularMarketChangePercent
												)})"
											>
												{quote.regularMarketChangePercent.toLocaleString('default', {
													style: 'decimal',
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
													signDisplay: 'exceptZero'
												})}%
											</span>
										</Button>
									{/each}
								</div>
								{#if !$userStore.signedIn}
									<div class="card-regular">
										Follow your favourite stocks.
										<a href="/signup" class="hyperlink card-bold"> Create free account </a>
									</div>
								{/if}
							</section>
						{/if}
					{/await}

					{#if beansproutReportsCount !== 0 && symbol}
						<section class="section-mb mb-3">
							<BeansproutReports
								ticker={symbol.exchangeTicker}
								showPastReports={false}
								bind:beansproutReportsCount
							/>
						</section>
					{/if}

					<section class="section-mb">
						<ArticleContent
							key={title}
							{content}
							category={categoryUid}
							{themes}
							{disableArticleAd}
						/>
					</section>

					{#if cta}
						<ArticleCta {...cta} />
					{/if}

					<section class="mb-8">
						<SocialShare {title} href={page.url.href} />
					</section>

					<section class="section-mb">
						<header class="mb-6">
							<h3 class="h2">Read also</h3>
						</header>

						{#await getRelatedArticles(article.documentId) then relatedArticles}
							<ul class="ml-4 grid list-disc gap-4">
								{#each relatedArticles as article, i}
									<li>
										<a
											href={`/${article.slug}`}
											data-type="article-read-also"
											data-content={i + 1}
											class="link body-bold"
										>
											{article.title}
										</a>
									</li>
								{/each}
							</ul>
						{/await}
					</section>

					{#if !disableArticleAd}
						<div class="dianomi_context section-mb" data-dianomi-context-id="3851"></div>
					{/if}

					{#if article.category.uid !== 'promo'}
						<section class="section-mb">
							<header class="mb-6">
								<h3 class="h2">Most Popular</h3>
							</header>

							{#await getTrendingArticles() then trendingArticles}
								<ul class="divide-gray-faint -mt-4 list-none divide-y">
									{#each trendingArticles as article, i}
										<li class="flex items-center py-4">
											<div class="h1-serif text-green-dark mr-4">
												{String(i + 1).padStart(2, '0')}
											</div>
											<a
												href={`/${article.slug}`}
												data-type="article-popular"
												data-content={i + 1}
												class="link body-bold block"
											>
												{article.title}
											</a>
										</li>
									{/each}
								</ul>
							{/await}
						</section>
					{/if}

					{#if !$userStore.signedIn}
						<FollowAndSubscription />
					{/if}

					<CommentsSection
						type={type === 'ProductReview' ? 'question' : 'comment'}
						documentId={article.documentId}
						{comments}
						formData={data.form}
					/>

					{#if !disableArticleAd}
						<section class="section-mb flex justify-center">
							<SponsorBanner />
						</section>
					{/if}
				</div>
			</article>
			<aside class="text-gray-dark relative hidden text-sm xl:block">
				<div class="sticky top-32 grid w-96">
					{#if stickyCta}
						{#if article.category.uid === 'promo'}
							<StickyPromo {stickyCta} device="desktop" />
						{:else}
							<StickyCta {stickyCta} device="desktop" />
						{/if}
					{:else if !disableArticleAd}
						<div class="mx-auto mb-5 w-80">
							<SiderailBanner location="article" />
						</div>
						<div class="dianomi_context" data-dianomi-context-id="3852"></div>
					{/if}
				</div>
			</aside>
		</div>
	</Container>
</div>

{#if stickyCta}
	{#if article.category.uid === 'promo'}
		<StickyPromo {stickyCta} device="mobile" />
	{:else}
		<StickyCta {stickyCta} device="mobile" />
	{/if}
{:else if !disableArticleAd}
	<StickyBanner location="article" />
{/if}
